import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsersCog, faSearch, faSync, faIdCard, faCheck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const AdminProfileAssign = () => {
    const [admins, setAdmins] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [savingId, setSavingId] = useState(null);

    const userRole = useMemo(() => {
        const adminData = JSON.parse(localStorage.getItem('dm_admin') || '{}');
        return adminData.role || 'ADMIN';
    }, []);

    const isSuper = userRole === 'SUPERADMIN';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Usamos promesas para cargar ambas listas en paralelo
            const pAdmins = new Promise(resolve => request.Get(API_BASE + 'superadmin/admins', {}, resolve, 'json', resolve));
            const pProfiles = new Promise(resolve => request.Get(API_BASE + 'superadmin/profiles', {}, resolve, 'json', resolve));
            
            const [rAdmins, rProfiles] = await Promise.all([pAdmins, pProfiles]);
            
            if (rAdmins && rAdmins.length !== undefined) setAdmins(rAdmins);
            else if (rAdmins && rAdmins.success && rAdmins.data) setAdmins(rAdmins.data);
            else if (Array.isArray(rAdmins)) setAdmins(rAdmins);
            
            if (rProfiles && rProfiles.success) setProfiles(rProfiles.data || []);
        } catch (e) {
            Notify({ type: 'fail', text: 'Error al cargar datos' });
        }
        setLoading(false);
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return admins.filter(a => !q || 
            a.AdminNombre?.toLowerCase().includes(q) || 
            a.AdminApellido?.toLowerCase().includes(q) ||
            a.AdminEmail?.toLowerCase().includes(q)
        );
    }, [admins, search]);

    const handleProfileChange = (adminId, profileId) => {
        setSavingId(adminId);
        request.Put(API_BASE + `superadmin/admins/${adminId}/profile`, { profileId: profileId || null }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Perfil asignado correctamente' });
                setAdmins(prev => prev.map(a => a.AdminId === adminId ? { ...a, ProfileId: profileId ? parseInt(profileId) : null } : a));
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al asignar perfil' });
            }
            setSavingId(null);
        }, 'json', () => {
            Notify({ type: 'fail', text: 'Error de conexión' });
            setSavingId(null);
        });
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="sa-search-bar" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color: '#475569' }} />
                    <input placeholder={isSuper ? "Buscar administrador..." : "Buscar staff..."} value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <button className="sa-btn sa-btn-ghost" onClick={fetchData} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                </div>
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>{isSuper ? 'Administrador' : 'Usuario Staff'}</th>
                                    <th>Email</th>
                                    <th>Empresa</th>
                                    <th>Perfil Asignado</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={5} className="text-center py-4 text-muted">No se encontraron {isSuper ? 'administradores' : 'usuarios staff'}</td></tr>
                                )}
                                {filtered.map(a => (
                                    <tr key={a.AdminId}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: 10,
                                                    background: 'rgba(168,85,247,0.12)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'var(--sa-primary)', fontSize: '0.8rem'
                                                }}>
                                                    <FontAwesomeIcon icon={faUsersCog} />
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                                    {a.AdminNombre} {a.AdminApellido}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                            {a.AdminEmail}
                                        </td>
                                        <td>
                                            <span className="sa-badge sa-badge-basic">{a.EmpresaNombre || 'Sin Empresa'}</span>
                                        </td>
                                        <td style={{ width: '250px' }}>
                                            <select 
                                                className="sa-input" 
                                                value={a.ProfileId || ''} 
                                                onChange={e => handleProfileChange(a.AdminId, e.target.value)}
                                                disabled={savingId === a.AdminId}
                                                style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                            >
                                                <option value="">-- Sin Perfil Asignado --</option>
                                                {profiles.map(p => (
                                                    <option key={p.ProfileId} value={p.ProfileId}>
                                                        {p.ProfileName}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            {savingId === a.AdminId ? (
                                                <Spinner size="sm" animation="border" style={{ color: 'var(--sa-primary)' }} />
                                            ) : (
                                                a.ProfileId ? 
                                                <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981' }} /> : 
                                                <FontAwesomeIcon icon={faTimes} style={{ color: '#ef4444' }} />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminProfileAssign;
